import { ReportStatus } from "../../../api/types";
import * as client from "../../../client";
import StatusNames from "../../../mappings_dir/statuses.json";
import LuminorAppetiteInfo from "../../../mappings_dir/luminor_appetite.json";
import IcmConstructionTypeMapping from "../../../mappings_dir/icm_construction_types.json";

export const TEMP_PIPELINE_EXAMPLES = new Set([
  "BentallGreenOak (U.S.) Limited Partnership",
  "Marathon Patent Group Inc",
]);

export const readableStatus = (status: ReportStatus): string => {
  return StatusNames.statuses[status];
};

export const statusIsClosedOrDeclined = (status: client.status): boolean => {
  return ["declined", "closed"].includes(status);
};

export const readableAppetiteFromScore = (score: number): string => {
  switch (score) {
    case 1:
      return "Out of Appetite";
    case 2:
      return "Missing Required Fields";
    case 3:
      return "Uncertain";
    case 4:
      return "Build";
    case 5:
      return "In Appetite";
  }
  return "Unknown Appetite";
};

export const numericAppetiteScore = (appetite: client.appetite_result) => {
  return LuminorAppetiteInfo.appetite_score[appetite];
};

export const readableAppetiteShort = (appetite: client.appetite_result) => {
  return LuminorAppetiteInfo.appetite_string_short[appetite];
};

export const readableAppetiteLong = (appetite: client.appetite_result) => {
  return LuminorAppetiteInfo.appetite_string_long[appetite];
};

export const getAllStatuses = (): client.status[] => {
  return Object.values(client.status);
};

export const isClearanceStatus = (status: client.status): boolean => {
  const clearanceStatuses: client.status[] = [
    "cleared",
    "not_cleared_effective_date_too_far",
    "not_cleared_incomplete_submission",
    "not_cleared_out_of_appetite",
    "not_cleared_unapproved_broker",
    "not_cleared_prior_submission",
    "not_cleared_low_tiv",
    "not_cleared_foreign_exposure",
  ];
  return clearanceStatuses.includes(status);
};

// Shared cell padding for DataTable columns across dashboards.
export const columnPtOptions = {
  bodyCell: { style: { padding: "6px 8px" } },
};

export type Severity = "good" | "bad" | "medium" | "other" | "neutral";

export const premiumSeverity = (premium: number): Severity => {
  if (premium >= 10_000_000) return "bad";
  if (premium >= 1_000_000) return "medium";
  if (premium >= 100_000) return "other";
  return "good";
};

export const colorForAttributes = (
  severity: Severity,
  isLightMode: boolean
) => {
  switch (severity) {
    case "good":
      return isLightMode ? "var(--green-100)" : "#0e4f2644";
    case "bad":
      return isLightMode ? "var(--red-100)" : "#66181444";
    case "medium":
      return isLightMode ? "var(--yellow-100)" : "#5e480344";
    case "other":
      return isLightMode ? "var(--cyan-100)" : "#18346244";
    default:
      return "var(--surface-100)";
  }
};

export const borderColorForAttributes = (
  severity: Severity,
  isLightMode: boolean
) => {
  switch (severity) {
    case "good":
      return isLightMode ? "var(--green-400)" : "var(--green-700)";
    case "bad":
      return isLightMode ? "var(--red-400)" : "var(--red-700)";
    case "medium":
      return isLightMode ? "var(--yellow-400)" : "var(--yellow-700)";
    case "other":
      return isLightMode ? "var(--cyan-400)" : "var(--cyan-700)";
    default:
      return "var(--surface-300)";
  }
};

export const colorForConfidence = (
  confidence: string,
  isLightMode: boolean
) => {
  switch (confidence) {
    case "low":
      return isLightMode
        ? HIGHLIGHT_ERROR_LIGHT_MODE
        : HIGHLIGHT_ERROR_DARK_MODE;
    case "medium":
      return isLightMode
        ? HIGHLIGHT_WARNING_LIGHT_MODE
        : HIGHLIGHT_WARNING_DARK_MODE;
    case "user edited":
      return isLightMode
        ? HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE
        : HIGHLIGHT_USER_OVERRIDE_DARK_MODE;
    default:
      return undefined;
  }
};

export const HIGHLIGHT_ERROR_DARK_MODE = "var(--red-900)";
export const HIGHLIGHT_WARNING_DARK_MODE = "var(--yellow-900)";
export const HIGHLIGHT_USER_OVERRIDE_DARK_MODE = "var(--blue-800)";
export const HIGHLIGHT_ERROR_LIGHT_MODE = "var(--red-100)";
export const HIGHLIGHT_WARNING_LIGHT_MODE = "var(--yellow-200)";
export const HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE = "var(--blue-100)";

export const LOW_CONFIDENCE_MINIMUM_SEVERITY = 80;

export const flaggedHighlightColor = (
  maxSeverity: number,
  isLightMode: boolean,
  hasOverride: boolean,
  isRequiredAndIsBlank?: boolean
) => {
  // Required and blank takes precedence over edits
  if (isRequiredAndIsBlank) {
    if (isLightMode) {
      return HIGHLIGHT_ERROR_LIGHT_MODE;
    }
    return HIGHLIGHT_ERROR_DARK_MODE;
  }

  // Edits take precedence over flags
  if (hasOverride) {
    if (isLightMode) {
      return HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE;
    }
    return HIGHLIGHT_USER_OVERRIDE_DARK_MODE;
  }

  // Now show flags
  if (maxSeverity > LOW_CONFIDENCE_MINIMUM_SEVERITY) {
    if (isLightMode) {
      return HIGHLIGHT_ERROR_LIGHT_MODE;
    }
    return HIGHLIGHT_ERROR_DARK_MODE;
  }
  if (isLightMode) {
    return HIGHLIGHT_WARNING_LIGHT_MODE;
  }
  return HIGHLIGHT_WARNING_DARK_MODE;
};

export const icmConstructionTypeMapping = () => {
  return IcmConstructionTypeMapping.construction_types;
};
